"use client";

import { Suspense, useEffect, useRef, useState } from "react";
import { useOspLogin } from "@features/auth/compat/useOspLogin";
import type { Web3LoginSupportedType } from "@features/auth/compat/sdk-types";
import { useSnapshot } from "valtio";
import { LottieLoading } from "@ospweb/sdk-ui";
import { deviceDetector } from "utils";
import { LoginStore, clearJustLoggedOutFlag } from "@store/modules/login";
import { authStore } from "@features/auth/store/auth-store";
import { EmailLoginModal } from "@components/EmailLogin";
import { SignInIntroPhase } from "@components/SignInIntro";
import { getBridge } from "@/src/lib/bridge";
import { MessageType } from "@/src/lib/bridge/types";
import { useOnboardingHarnessQuery } from "@/src/hooks/onboarding/useOnboardingHarnessQuery";
import { trackAuthSigninSuccess } from "@/src/hooks/onboarding/onboardingAnalytics";
import { resolveOverlapSigninExtras } from "@features/etf-overlap/tracking/authSigninSuccess";
import { Track } from "@/src/lib/tracking";
import {
  consumeAuthSigninEntry,
  peekAuthSigninEntry,
  setAuthSigninEntry,
} from "@/src/hooks/onboarding/authSigninEntry";
import { resolveIntroAuthPlatform } from "@components/SignInIntro/resolveIntroAuthPlatform";
import {
  getThemePackCampaign,
  THEME_PACK_CATALOG,
} from "@utils/onboarding/themePackCatalog";
import {
  AD_SOURCE_ETF_OVERLAP,
  AD_SOURCE_THEME_RANK,
} from "@utils/onboarding/adSource";
import { peekEntryIntent } from "@utils/onboarding/entryIntent.client";
import {
  getAdSourceCookie,
  getThemePackLandingIdCookie,
} from "@utils/onboarding/adSourceCookie.client";
import { hasLinkInviteCodeAtEntry } from "@utils/invite/inviteCodeCookie.client";

type LoginPagePhase = "intro" | "login";

function resolveInitialLoginPhase(
  harness: ReturnType<typeof useOnboardingHarnessQuery>,
): LoginPagePhase {
  if (harness.enabled && harness.loginPhase === "login") {
    return "login";
  }
  return "intro";
}

function mapLoginTypeToMethod(
  loginType: string | undefined | null,
): "apple" | "google" | "email" {
  const normalized = String(loginType ?? "").toLowerCase();
  if (normalized.includes("apple")) {
    return "apple";
  }
  if (normalized.includes("google")) {
    return "google";
  }
  return "email";
}

/** 独立登录页：Phase A Intro → Phase B EmailLoginFlow（D12） */
function LoginPageContent() {
  const harness = useOnboardingHarnessQuery();
  const { login } = useOspLogin();
  const gateReadySentRef = useRef(false);
  const authSuccessTrackedRef = useRef(false);
  const sawLoggedOutRef = useRef(false);
  const themeIntroViewedRef = useRef<string | null>(null);
  const [phase, setPhase] = useState<LoginPagePhase>(() =>
    resolveInitialLoginPhase(harness),
  );
  const [entryReady, setEntryReady] = useState(false);
  const [themePackLandingId, setThemePackLandingId] = useState<string | null>(
    null,
  );
  /**
   * 本次进入是否带着链接来的邀请码（fe-spec D26）—— 只在挂载时判定一次，
   * 之后手动填码、链接码被判无效都不再切换起始页布局。
   */
  const [inviteLinkEntry, setInviteLinkEntry] = useState(false);
  // EDG-561：ETF 重叠广告会话；与 entryReady 同一个 effect 里算，避免 SSR 水合不一致
  const [isEtfOverlapAd, setIsEtfOverlapAd] = useState(false);
  const themePackCampaign = getThemePackCampaign(themePackLandingId);
  const themePackShortName = themePackCampaign
    ? THEME_PACK_CATALOG[themePackCampaign].shortName
    : null;

  useEffect(() => {
    const themePackId =
      getAdSourceCookie() === AD_SOURCE_THEME_RANK
        ? getThemePackLandingIdCookie()
        : null;
    setThemePackLandingId(themePackId);
    const fromInviteLink = hasLinkInviteCodeAtEntry(
      window.location.pathname,
      window.location.search,
    );
    setInviteLinkEntry(fromInviteLink);
    // 历史登录用户的「直接跳 login」对广告会话是例外：广告用户必须先看到承接 Intro（RK-16）
    const etfOverlapAd =
      getAdSourceCookie()?.trim().toUpperCase() === AD_SOURCE_ETF_OVERLAP ||
      peekEntryIntent() === "etf_overlap";
    setIsEtfOverlapAd(etfOverlapAd);
    setEntryReady(true);
    const hasLoginHistory = localStorage.getItem("edw.last_login_type");
    if (hasLoginHistory && !themePackId && !etfOverlapAd && phase === "intro") {
      setPhase("login");
    }
  }, []);
  const {
    isLoading: isBusinessLoading,
    isSocialLoginIntent,
    isFetching,
    isLoggedIn,
    loginType,
  } = useSnapshot(LoginStore);

  const loginController = async (type: Web3LoginSupportedType) => {
    LoginStore.isSocialLoginIntent = true;
    try {
      await login(type);
    } catch (error) {
      // 失败埋点（auth_signin_error / auth_provider_result）已由 AuthOrchestrator 统一发，这里只留排障日志；
      // 调用方是 `void loginController(...)`，不接住就是 unhandled rejection
      Track.log("error", "LoginPage", "social login failed", {
        type,
        cause: String(error),
      });
    } finally {
      LoginStore.isSocialLoginIntent = false;
    }
  };

  useEffect(() => {
    if (!harness.enabled) {
      return;
    }
    if (harness.loginPhase === "login") {
      setPhase("login");
    } else if (harness.loginPhase === "intro") {
      setPhase("intro");
    }
  }, [harness.enabled, harness.loginPhase]);

  const activePhase: LoginPagePhase =
    harness.enabled && harness.loginPhase != null ? harness.loginPhase : phase;

  useEffect(() => {
    if (
      activePhase !== "intro" ||
      !themePackLandingId ||
      themeIntroViewedRef.current === themePackLandingId
    ) {
      return;
    }
    themeIntroViewedRef.current = themePackLandingId;
    Track.view("pack_intro_view", {
      landing_id: themePackLandingId,
      theme_id: themePackCampaign
        ? THEME_PACK_CATALOG[themePackCampaign].dataSlug
        : undefined,
    });
  }, [activePhase, themePackLandingId, themePackCampaign]);

  useEffect(() => {
    if (isFetching || isLoggedIn) {
      return;
    }
    clearJustLoggedOutFlag();
  }, [isFetching, isLoggedIn]);

  useEffect(() => {
    if (!deviceDetector.isApp()) {
      return;
    }
    if (isFetching || isLoggedIn) {
      return;
    }
    if (gateReadySentRef.current) {
      return;
    }
    gateReadySentRef.current = true;
    getBridge().send(MessageType.WEB_LOGIN_GATE_READY, {
      source: "login_page",
      loginState: "needs_login",
    });
  }, [isFetching, isLoggedIn]);

  // 本页生命周期内先确认过「未登录」（恢复流程已结束且无会话），之后变成已登录才算一次登录成功。
  // 已登录用户直接打开 /login（会话恢复、壳跳转）会以 isLoggedIn=true 进入或从恢复中直接变 true，不能计成功。
  useEffect(() => {
    if (!isFetching && !isLoggedIn) {
      sawLoggedOutRef.current = true;
    }
  }, [isFetching, isLoggedIn]);

  useEffect(() => {
    if (
      !isLoggedIn ||
      !sawLoggedOutRef.current ||
      authSuccessTrackedRef.current
    ) {
      return;
    }
    authSuccessTrackedRef.current = true;
    const entry = consumeAuthSigninEntry("signin_page");
    const platform = resolveIntroAuthPlatform();
    const landingId =
      getAdSourceCookie() === AD_SOURCE_THEME_RANK
        ? getThemePackLandingIdCookie()
        : null;
    // 登录方式以 authStore 为准：新 auth 模块登录时不写 LoginStore.loginType，只靠它会把
    // Apple / Google 全部落成 email。LoginStore 只作缓存恢复场景的兜底
    const authStatus = authStore.status;
    const base = {
      platform,
      method:
        authStatus.state === "authenticated"
          ? authStatus.user.loginType
          : mapLoginTypeToMethod(
              loginType != null ? String(loginType) : undefined,
            ),
      entry,
      ...(landingId
        ? { landing_id: landingId, ad_source: AD_SOURCE_THEME_RANK }
        : {}),
    };
    // 重叠广告会话异步补 ad_source / has_holdings；其余会话原样同步发，载荷不变（EDG-561 AC-1.5）
    const extras = resolveOverlapSigninExtras();
    if (extras) {
      void extras.then((extra) =>
        trackAuthSigninSuccess({ ...base, ...extra }),
      );
    } else {
      trackAuthSigninSuccess(base);
    }
  }, [isLoggedIn, loginType]);

  useEffect(() => {
    if (activePhase !== "login") {
      return;
    }
    // Sign in 页入口：若尚未标记（历史用户跳过 Intro），记为 signin_page
    if (!peekAuthSigninEntry()) {
      setAuthSigninEntry("signin_page");
    }
  }, [activePhase]);

  if (!entryReady) {
    return null;
  }

  if (isLoggedIn) {
    return (
      <div className="flex h-dvh w-full items-center justify-center">
        <LottieLoading style={{ width: 80, height: 80 }} />
      </div>
    );
  }

  if (activePhase === "intro") {
    return (
      <SignInIntroPhase
        themePack={
          themePackLandingId
            ? { themeShortName: themePackShortName }
            : undefined
        }
        inviteLinkEntry={inviteLinkEntry}
        adIntro={isEtfOverlapAd ? "etf_overlap" : undefined}
        initialSlideIndex={harness.introSlide ?? 0}
        freezeCarousel={harness.enabled && harness.introSlide !== null}
        onGetStarted={() => setPhase("login")}
        onApple={() => {
          void loginController("APPLE");
        }}
        onGoogle={() => {
          void loginController("GOOGLE");
        }}
      />
    );
  }

  return (
    <EmailLoginModal
      open
      mode="page"
      onClose={() => {}}
      onSocialClick={(type) => {
        setAuthSigninEntry("signin_page");
        void loginController(type);
      }}
      loading={isBusinessLoading || !!isSocialLoginIntent}
      hideCloseButton
    />
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={null}>
      <LoginPageContent />
    </Suspense>
  );
}
